import type {
  DesktopAppRow,
  DesktopAppsResponse,
  ImpactResponse,
  TeamInsightsFilters,
  TeamMembersDailyPoint,
  TeamMembersMode,
  TeamMembersResponse,
} from '@/api/teamInsights'
import type { EnterpriseMemberRowResponse } from '@/api/teamMembersTable'

const EMPTY_FILTERS: TeamInsightsFilters = {} as TeamInsightsFilters

const TEAM_MEMBERS_PATTERN: number[] = [
  4, 5, 5, 7, 6, 8, 7, 9, 8, 11, 10, 12, 11, 13, 12, 14, 13, 15, 14, 16, 15, 18, 17, 19, 18, 20, 19, 21, 20, 22,
]

export const buildMockTeamMembersResponse = (mode: TeamMembersMode): TeamMembersResponse => {
  const today = new Date()
  today.setUTCHours(0, 0, 0, 0)
  const timeseries: TeamMembersDailyPoint[] = TEAM_MEMBERS_PATTERN.map((count, i) => {
    const d = new Date(today)
    d.setUTCDate(d.getUTCDate() - (TEAM_MEMBERS_PATTERN.length - 1 - i))
    return { period: d.toISOString().slice(0, 10), count }
  })
  return {
    filters_applied: EMPTY_FILTERS,
    total_members: 22,
    pct_change_vs_prev_period: null,
    mode,
    timeseries,
  }
}

// Capped at 10 to match the chart's ROW_LIMIT so the locked preview never
// renders the "See all" CardAction button.
const APP_KEYS = ['Slack', 'Chrome', 'Notion', 'Gmail', 'Linear', 'VS Code', 'Figma', 'Zoom', 'Outlook', 'Safari']

export const buildMockDesktopAppsResponse = (): DesktopAppsResponse => {
  const totals = [82000, 64000, 51000, 43000, 36000, 29000, 24000, 19000, 15000, 12000]
  const grandTotal = totals.reduce((a, b) => a + b, 0)
  const rows: DesktopAppRow[] = APP_KEYS.map((app_key, i) => ({
    app_key,
    category: 'productivity',
    word_count: totals[i],
    pct_of_total: (totals[i] / grandTotal) * 100,
    user_count: 0,
  }))
  return {
    filters_applied: EMPTY_FILTERS,
    distinct_app_count: 150,
    distinct_app_count_pct_change_vs_prev_period: null,
    rows,
    total_distinct_app_count: 150,
  }
}

const DAY_MS = 86_400_000

const MOCK_MEMBER_ROWS: Array<
  Omit<EnterpriseMemberRowResponse, 'lastUsedAt' | 'joinedAt'> & { daysAgo: number; joinedDaysAgo: number }
> = [
  {
    userId: 'mock-1',
    firstName: 'Ethan',
    lastName: 'Brooks',
    email: 'ethan@example.com',
    role: 'admin',
    status: 'active',
    trialEndsAt: null,
    teamId: 'mock-eng',
    teamName: 'Engineering',
    totalWords: 184_320,
    wordsInWindow: null,
    wpm: 142,
    daysAgo: 0,
    joinedDaysAgo: 365,
  },
  {
    userId: 'mock-2',
    firstName: 'Maya',
    lastName: 'Collins',
    email: 'maya@example.com',
    role: 'member',
    status: 'active',
    trialEndsAt: null,
    teamId: 'mock-design',
    teamName: 'Design',
    totalWords: 142_870,
    wordsInWindow: null,
    wpm: 128,
    daysAgo: 1,
    joinedDaysAgo: 300,
  },
  {
    userId: 'mock-3',
    firstName: 'Liam',
    lastName: 'Foster',
    email: 'liam@example.com',
    role: 'member',
    status: 'active',
    trialEndsAt: null,
    teamId: 'mock-sales',
    teamName: 'Sales',
    totalWords: 98_412,
    wordsInWindow: null,
    wpm: 116,
    daysAgo: 2,
    joinedDaysAgo: 250,
  },
  {
    userId: 'mock-4',
    firstName: 'Zoe',
    lastName: 'Mitchell',
    email: 'zoe@example.com',
    role: 'member',
    status: 'trialing',
    trialEndsAt: Math.floor((Date.now() + 5 * DAY_MS) / 1000),
    teamId: 'mock-pm',
    teamName: 'Product',
    totalWords: 64_205,
    wordsInWindow: null,
    wpm: 104,
    daysAgo: 3,
    joinedDaysAgo: 14,
  },
  {
    userId: 'mock-5',
    firstName: 'Noah',
    lastName: 'Hayes',
    email: 'noah@example.com',
    role: 'member',
    status: 'active',
    trialEndsAt: null,
    teamId: 'mock-eng',
    teamName: 'Engineering',
    totalWords: 51_938,
    wordsInWindow: null,
    wpm: 98,
    daysAgo: 4,
    joinedDaysAgo: 180,
  },
  {
    userId: 'mock-6',
    firstName: 'Ava',
    lastName: 'Reynolds',
    email: 'ava@example.com',
    role: 'it_admin',
    status: 'active',
    trialEndsAt: null,
    teamId: 'mock-ops',
    teamName: 'Operations',
    totalWords: 37_104,
    wordsInWindow: null,
    wpm: 89,
    daysAgo: 7,
    joinedDaysAgo: 90,
  },
]

export const buildMockTeamMembersTableRows = (): EnterpriseMemberRowResponse[] => {
  const now = Date.now()
  return MOCK_MEMBER_ROWS.map(({ daysAgo, joinedDaysAgo, ...row }) => ({
    ...row,
    lastUsedAt: Math.floor((now - daysAgo * DAY_MS) / 1000),
    joinedAt: Math.floor((now - joinedDaysAgo * DAY_MS) / 1000),
  }))
}

export const buildMockImpactResponse = (): ImpactResponse => ({
  filters_applied: EMPTY_FILTERS,
  minutes_saved: { total: 12_345, since: '2025-01-01' },
  ai_prompt_quality: { multiplier: 2.2 },
  dictionary: {
    total: 184,
    auto_learned: 120,
    manually_added: 32,
    phrases_added: 18,
    snippets_added: 14,
  },
  auto_formatting: {
    total: 9_876,
    by_category: [
      { category: 'capitalization', count: 4200 },
      { category: 'punctuation', count: 2800 },
      { category: 'spacing', count: 1500 },
      { category: 'numbers', count: 900 },
      { category: 'other', count: 476 },
    ],
  },
  features_utilized: [
    { feature: 'dictionary', user_count: 20, feature_usage_count: 318 },
    { feature: 'transforms', user_count: 22, feature_usage_count: 0 },
    { feature: 'snippets', user_count: 14, feature_usage_count: 153 },
    { feature: 'styles', user_count: 11, feature_usage_count: 87 },
  ],
  value_calculator: {
    team_member_count: 32,
    monthly_minutes_saved: 4_800,
    monthly_cost_minor_units: 96_000,
    currency: 'USD',
  },
})
